/**
 * Checks if a digital fee should be applied for given publisher codes
 * @param {string | string[]} publisherCodes - Publisher code(s) to check
 * @returns {Promise<Array<{code: string, isApplied: boolean}>>}
 */
function queryDigitalDeliveryFeesApi(publisherCodes) {
  if (
    window.digital_product_config == undefined ||
    window.digital_product_config?.api_url == undefined ||
    window.digital_product_config?.store_number == undefined
  ) {
    throw Error("Digital fee config does not exist");
  }

  const api_url = window.digital_product_config.api_url;
  const store_number = window.digital_product_config.store_number;

  let uniqueCodes;
  if (typeof publisherCodes === "string") {
    uniqueCodes = [
      ...new Set(
        publisherCodes
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  } else {
    uniqueCodes = [
      ...new Set(
        publisherCodes.map((c) => String(c).trim()).filter(Boolean)
      ),
    ];
  }
  const queryValue = uniqueCodes.join("|");

  if (queryValue !== "") {
    const requestUrl = `${api_url}?storeNumber=${store_number}&downloadprovidercode=${queryValue}`;
    return fetch(requestUrl)
      .then((res) => res.json())
      .then((data) => {
        console.log("[digital-fee] API request:", requestUrl);
        console.log("[digital-fee] API raw response:", data);
        const mapped = data.map((entry) => {
          return {
            code: entry.code,
            isApplied: entry.isApplied ?? false,
          };
        });
        console.log("[digital-fee] API mapped (code + isApplied):", mapped);
        return mapped;
      })
      .catch((e) => {
        console.error("[digital-fee] API request failed:", e);
        return [];
      });
  } else {
    return Promise.resolve([]);
  }
}

/** All `.purchase-checkbox` nodes whose `data-variant-id` matches (safe vs. CSS selector quirks). */
function cmrpFindPurchaseCheckboxesByVariantId(variantId) {
  const want = String(variantId || "");
  if (!want) return [];
  return Array.from(document.querySelectorAll("input.purchase-checkbox")).filter(
    (el) => el.getAttribute("data-variant-id") === want
  );
}

// Digital delivery fee API: set `data-variant-notice` on matching digital rows (CMRP inline notice).
// Multiple variants can share one publisher code (e.g. unified-view merged digitals) — map code → many variant ids.
async function applyDigitalDeliveryFeeVariantNotices() {
  const checkboxContainers = document.querySelectorAll(
    ".purchase-checkbox-container"
  );

  if (checkboxContainers.length === 0) {
    return;
  }

  /** @type {Map<string, Set<string>>} */
  const publisherToVariantIds = new Map();
  checkboxContainers.forEach((container) => {
    const input = container.querySelector("input.purchase-checkbox");

    if (!input) return;

    const isDigital = input.dataset.isDigital === "true";
    const publisherCode = input.dataset.publisherCode;
    const variantId = input.getAttribute("data-variant-id");

    if (
      isDigital &&
      publisherCode &&
      publisherCode !== "null" &&
      publisherCode.trim() !== ""
    ) {
      const code = publisherCode.trim();
      if (!publisherToVariantIds.has(code)) {
        publisherToVariantIds.set(code, new Set());
      }
      if (variantId) {
        publisherToVariantIds.get(code).add(variantId);
      }
    }
  });

  const publisherCodesToCheck = Array.from(publisherToVariantIds.keys());
  if (publisherCodesToCheck.length === 0) {
    return;
  }

  queryDigitalDeliveryFeesApi(publisherCodesToCheck).then((results) => {
    if (!Array.isArray(results)) {
      return;
    }

    console.log(
      "[digital-fee] Tooltip visibility pass; unhide when isApplied is true:",
      results
    );

    const noticeText = window.digital_product_config?.notice_text;
    const collapsesToSync = new Set();

    if (noticeText) {
      results.forEach((result) => {
        const isApplied = result.isApplied ?? false;
        if (!isApplied) return;

        const variantIds = publisherToVariantIds.get(result.code);
        if (!variantIds || variantIds.size === 0) return;

        variantIds.forEach((vid) => {
          cmrpFindPurchaseCheckboxesByVariantId(vid).forEach((checkbox) => {
            const row = checkbox.closest(".purchase-type");
            if (row) {
              row.setAttribute("data-variant-notice", noticeText);
              const collapse = row.closest(".collapse-content");
              if (collapse) collapsesToSync.add(collapse);
            }
          });
        });
      });
    }

    const appliedCodes = new Set();
    results.forEach((result) => {
      if (result.isApplied ?? false) {
        appliedCodes.add(result.code);
      }
    });

    checkboxContainers.forEach((container) => {
      const input = container.querySelector("input.purchase-checkbox");
      if (!input) return;

      const isDigital = input.dataset.isDigital === "true";
      const publisherCode = input.dataset.publisherCode;

      if (
        !isDigital ||
        !publisherCode ||
        publisherCode === "null" ||
        !publisherCode.trim() ||
        !appliedCodes.has(publisherCode.trim())
      ) {
        return;
      }

      const row = container.closest(".purchase-type");
      const tooltip = row?.querySelector(".notice-tooltip");
      if (tooltip) {
        tooltip.classList.remove("hidden");
      }
    });

    if (
      collapsesToSync.size > 0 &&
      typeof window.syncCmrpVariantNoticeInCollapse === "function"
    ) {
      collapsesToSync.forEach((c) =>
        window.syncCmrpVariantNoticeInCollapse(c)
      );
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", applyDigitalDeliveryFeeVariantNotices);
} else {
  applyDigitalDeliveryFeeVariantNotices();
}

document.addEventListener("product-display:updated", () => {
  applyDigitalDeliveryFeeVariantNotices();
});
